// --- React & hooks
import { useEffect, useMemo, useRef, useState, useCallback } from "react";

// --- MUI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import { IconButton } from "@mui/material";
// Wrapper components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Icons
import AssessmentIcon from "@mui/icons-material/Assessment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import InsightsIcon from "@mui/icons-material/Insights";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import NotesIcon from "@mui/icons-material/Notes";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";

import PropTypes from "prop-types";

// --- Recharts
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  ZAxis,
} from "recharts";
import { useTheme, useMediaQuery, FormControl, InputLabel, Select, MenuItem } from "@mui/material";

// --- D3 (for the donut)
import { select, arc, pie as d3pie, scaleOrdinal } from "d3";

// --- API
import { fetchDashboardData, fetchDataInsights } from "service/axiosConfig";

// ---------- Config ----------
const LINE_COLORS = ["#2563eb", "#da1480ff", "#16a34a", "#9333ea", "#f59e0b", "#06b6d4", "#64748b"];
const SCORE_LABEL = ["Very Poor", "Poor", "Fair", "Good", "Very Good"];

// ---------- Small D3 Donut component ----------
function OutcomesDonut({ data, width = 260, height = 220 }) {
  const ref = useRef(null);
  const colors = scaleOrdinal().range(["#16a34a", "#eab308", "#dc2626"]); // Completed, Incomplete, NoAnswer

  useEffect(() => {
    const svg = select(ref.current);
    svg.selectAll("*").remove();

    const radius = Math.min(width, height) / 2 - 6;
    const g = svg.append("g").attr("transform", `translate(${width / 2}, ${height / 2})`);

    const pieGen = d3pie()
      .sort(null)
      .value((d) => d.value);
    const arcs = pieGen(data);

    const arcGen = arc()
      .innerRadius(radius * 0.6)
      .outerRadius(radius);

    g.selectAll("path")
      .data(arcs)
      .enter()
      .append("path")
      .attr("d", arcGen)
      .attr("fill", (d, i) => colors(i))
      .attr("stroke", "white")
      .attr("stroke-width", 2);

    const total = data.reduce((a, b) => a + b.value, 0);
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "-0.2em")
      .style("font-weight", 700)
      .style("font-size", "16px")
      .text(total);

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "1.1em")
      .style("font-size", "11px")
      .style("fill", "#6b7280")
      .text("Total Calls");
  }, [data, height, width]);

  return (
    <svg ref={ref} width={width} height={height} role="img" aria-label="Call outcomes donut" />
  );
}

OutcomesDonut.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string.isRequired, value: PropTypes.number.isRequired })
  ).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
};
OutcomesDonut.defaultProps = { width: 260, height: 220 };

// ---------- Utilities ----------
const fmtShort = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { day: "2-digit", month: "2-digit" }); // DD/MM

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// ---------- Main ----------
export default function Reports() {
  const isMobile = useMediaQuery("(max-width:650px)");
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [topics, setTopics] = useState([]);
  const [period, setPeriod] = useState(14);
  const [insights, setInsights] = useState("");
  const [loadingInsights, setLoadingInsights] = useState(true);

  const handleRangeChange = (days) => {
    setPeriod(days);
  };

  useEffect(() => {
    let isMounted = true; // cleanup safety flag
    const userId = parseInt(localStorage.getItem("userId"), 10);

    // ⚡ Start both API calls immediately
    const dashboardPromise = fetchDashboardData(userId, period);
    const insightsPromise = fetchDataInsights(userId, period);

    // 🧩 1️⃣ As soon as dashboard data arrives — update UI instantly
    dashboardPromise
      .then((data) => {
        if (!isMounted) return;
        const safeData = data || [];
        setRows(safeData);
        setTopics([...new Set(safeData.map((d) => d.topic))]);
      })
      .catch((e) => console.error("Dashboard fetch failed", e));

    // 🧠 2️⃣ Then whenever AI insights arrive — update insights separately
    // 2️⃣ AI Insights (show loader while waiting)
    setLoadingInsights(true);
    insightsPromise
      .then((aiInsights) => {
        if (!isMounted) return;
        setInsights(aiInsights || {});
      })
      .catch((e) => console.error("AI insights fetch failed", e))
      .finally(() => {
        if (isMounted) setLoadingInsights(false);
      });

    // 🧹 cleanup
    return () => {
      isMounted = false;
    };
  }, [period]);

  // Dates & range
  const allDates = useMemo(() => {
    const s = new Set(rows.map((r) => fmtShort(r.date)));
    return [...s].sort((a, b) => {
      const [ad, am] = a.split("/").map(Number);
      const [bd, bm] = b.split("/").map(Number);
      // month/day sort
      return am === bm ? ad - bd : am - bm;
    });
  }, [rows]);

  const dateRangeLabel = useMemo(() => {
    if (!rows.length) return "";
    const sorted = [...rows].sort((a, b) => new Date(a.date) - new Date(b.date));
    const start = new Date(sorted[0].date);
    const end = new Date(sorted[sorted.length - 1].date);
    return `${start.getMonth() + 1}/${start.getDate()} – ${end.getMonth() + 1}/${end.getDate()}`;
  }, [rows]);

  // Daily outcomes (one outcome per day already)
  const dailyOutcomes = useMemo(() => {
    const byDate = new Map();
    rows.forEach((r) => {
      const d = fmtShort(r.date);
      if (!byDate.has(d)) byDate.set(d, r.status);
    });
    let completed = 0,
      incomplete = 0,
      noAnswer = 0;
    for (const st of byDate.values()) {
      if (st === "Complete") completed++;
      else if (st === "Incomplete") incomplete++;
      else if (st === "NoAnswerOrVoicemail") noAnswer++;
    }
    return { byDate, completed, incomplete, noAnswer };
  }, [rows]);

  // build alert dates once
  const alertDates = useMemo(() => {
    return new Set(
      rows
        .filter((r) => r.alert > 0) // only rows where alert > 0
        .map((r) => fmtShort(r.date)) // normalize to your short date format
    );
  }, [rows]);

  // pivot + add Alerts
  const trendSeries = useMemo(() => {
    const byDate = {};
    rows.forEach((r) => {
      const d = fmtShort(r.date);
      if (
        !byDate[d] &&
        r.status !== "NoAnswerOrVoicemail" &&
        r.status != "ConnectionError" &&
        r.status != "Incomplete"
      )
        byDate[d] = { date: d };
      if (
        r.status !== "NoAnswerOrVoicemail" &&
        r.status != "ConnectionError" &&
        r.status != "Incomplete"
      ) {
        byDate[d][r.topic] = r.score != null ? Number(r.score) : null;
      }
    });
    // ensure keys exist + attach Alerts
    Object.values(byDate).forEach((row) => {
      topics.forEach((t) => {
        if (!(t in row)) row[t] = null;
      });
      // ✅ detect alert for this date
      const alertTopics = topics.filter((t) => row[t] != null && row[t] <= 2 && row[t] > 0);
      row.hasAlert = alertTopics.length > 0 ? 1 : null; // 1 = marker, null = nothing
      row.alertTopics = alertTopics;
      if (alertDates.has(row.date)) {
        const scores = topics.map((t) => row[t]).filter((v) => v != null); // keep only actual numbers

        // fallback if no scores found
        row.Alerts = scores.length ? Math.min(...scores) : 0;
      } else {
        row.Alerts = null;
      }
    });
    return Object.values(byDate);
  }, [rows, topics, alertDates]);

  // Weekly summary KPIs
  const weeklyKPIs = useMemo(() => {
    const byTopic = {};
    const filtered = rows
      .filter((r) => r.status !== "NoAnswerOrVoicemail" && r.score != null)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    const last7 = filtered.slice(-7);
    const prev7 = filtered.slice(-14, -7);
    const avgFor = (arr, t) => {
      const subset = arr.filter((r) => r.topic === t);
      if (!subset.length) return 0;
      return subset.reduce((s, r) => s + r.score, 0) / subset.length;
    };
    topics.forEach((t) => {
      const avgLast = avgFor(last7, t);
      const avgPrev = avgFor(prev7, t);
      byTopic[t] = {
        avg: Number(avgLast.toFixed(2)),
        delta: Number((avgLast - avgPrev).toFixed(2)),
      };
    });
    return byTopic;
  }, [rows, topics]);

  // Distribution for a topic
  const scoreDistribution = (topic) => {
    const buckets = [0, 0, 0, 0, 0];
    rows
      .filter((r) => r.topic === topic && r.status !== "NoAnswerOrVoicemail" && r.score != null)
      .forEach((r) => {
        const s = clamp(r.score, 1, 5);
        buckets[s - 1] += 1;
      });
    return buckets.map((count, i) => ({ score: i + 1, count }));
  };

  // Colors
  const colorFor = (name) => {
    const i = topics.indexOf(name);
    return LINE_COLORS[(i + LINE_COLORS.length) % LINE_COLORS.length];
  };

  const handleTab = (_, v) => setTab(v);
  // 1) Add a flag
  const [isAlertHover, setIsAlertHover] = useState(false);

  // 2) A tiny custom dot just for the alert line that flips the flag
  const AlertDot = useCallback(
    ({ cx, cy, payload }) => {
      if (!payload?.hasAlert) return null;
      return (
        <g
          transform={`translate(${cx - 10}, ${cy + 10})`}
          onMouseEnter={() => setIsAlertHover(true)}
          onMouseLeave={() => setIsAlertHover(false)}
          style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
          <foreignObject x={0} y={0} width={24} height={24}>
            <ReportProblemIcon style={{ color: "#dc2626", fontSize: 24 }} />
          </foreignObject>
        </g>
      );
    },
    [setIsAlertHover]
  );

  // 3) Reset the flag when the mouse leaves the chart entirely
  //    (prevents a "stuck" state if someone zips off the icon fast)

  // 🧠 Smart fuzzy topic matcher
  const findMatchingTopicKey = (insightsTopics, topic) => {
    if (!insightsTopics || !topic) return null;

    const keys = Object.keys(insightsTopics);
    if (!keys.length) return null;

    const normalize = (s) =>
      s
        .toLowerCase()
        .replace(/[^\w\s]|_/g, "")
        .replace(/\s+/g, " ")
        .trim();

    const normalizedTopic = normalize(topic);

    // 1️⃣ exact match
    const exact = keys.find((k) => normalize(k) === normalizedTopic);
    if (exact) return exact;

    // 2️⃣ contains match (topic inside key or key inside topic)
    const contains = keys.find(
      (k) => normalize(k).includes(normalizedTopic) || normalizedTopic.includes(normalize(k))
    );
    if (contains) return contains;

    // 3️⃣ fallback: first key (avoid crash)
    //return keys[0];
  };

  return (
    <>
      <header
        style={{
          backgroundColor: "#561919",
          color: "#fff",
          padding: "1rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: "1.25rem" }}>Heartline</div>
      </header>

      <DashboardLayout>
        <MDBox pt={4} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                {/* Header strip */}
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  bgColor="rgb(86,25,25)"
                  borderRadius="lg"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDBox variant="" bgColor="rgb(86,25,25)" borderRadius="lg" display="flex">
                    <AssessmentIcon fontSize="medium" color="white" />
                    <MDTypography variant="h6" color="white" px={1}>
                      Reports
                    </MDTypography>
                  </MDBox>
                  {isMobile && (
                    <MDBox display="flex" alignItems="center" gap={0.5} width="100px" ml={3}>
                      <ScreenRotationIcon sx={{ fontSize: 12, color: "white !important" }} />
                      <MDTypography
                        variant="button"
                        color="white"
                        sx={{ fontSize: "0.6rem", fontWeight: 400 }}
                      >
                        Turn to landscape for full dashboard
                      </MDTypography>
                    </MDBox>
                  )}
                  <DashboardNavbar />
                </MDBox>

                {/* Tabs */}
                <MDBox p={3}>
                  {/* Tabs (responsive) */}
                  <MDBox p={3}>
                    {(() => {
                      const theme = useTheme();
                      const isMobile = useMediaQuery(theme.breakpoints.down("md"));

                      return (
                        <>
                          {/* Desktop View */}
                          {!isMobile && (
                            <Tabs
                              value={tab}
                              onChange={handleTab}
                              variant="scrollable"
                              scrollButtons="auto"
                            >
                              <Tab label="Trend Report" value={0} />
                              <Tab label="Weekly Summary" value={1} />
                              {topics.map((t, i) => (
                                <Tab key={t} label={t} value={2 + i} />
                              ))}
                              <Tab label="Sentiment" value={2 + topics.length} />
                            </Tabs>
                          )}

                          {/* Mobile View (Dropdown) */}
                          {isMobile && (
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                              <InputLabel id="report-type-label">Report Type</InputLabel>
                              <Select
                                labelId="report-type-label"
                                label="Report Type"
                                value={tab}
                                onChange={(e) => handleTab(e, e.target.value)}
                                sx={{
                                  backgroundColor: "#fff",
                                  fontWeight: 500,
                                  borderRadius: 1,
                                }}
                              >
                                <MenuItem value={0}>Trend Report</MenuItem>
                                <MenuItem value={1}>Weekly Summary</MenuItem>
                                {topics.map((t, i) => (
                                  <MenuItem key={t} value={2 + i}>
                                    {t}
                                  </MenuItem>
                                ))}

                                <Tab label="Sentiment" value={2 + topics.length} />
                              </Select>
                            </FormControl>
                          )}

                          <Divider sx={{ my: 2 }} />
                        </>
                      );
                    })()}
                  </MDBox>

                  {/* ========== TREND REPORT ========== */}
                  {tab === 0 && (
                    <MDBox>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                      >
                        {/* Left: Title */}
                        <MDTypography variant="h6" gutterBottom>
                          Health Trend Report
                        </MDTypography>

                        {/* Right: Time Range + Dates */}
                        <MDBox display="flex" flexDirection="column" alignItems="flex-end">
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="time-range-label">Time Range</InputLabel>
                            <Select
                              labelId="time-range-label"
                              value={period}
                              label="Time Range"
                              onChange={(e) => handleRangeChange(e.target.value)}
                            >
                              <MenuItem value={14}>2 Weeks</MenuItem>
                              <MenuItem value={30}>1 Month</MenuItem>
                              <MenuItem value={90}>3 Months</MenuItem>
                              <MenuItem value={365}>1 Year</MenuItem>
                            </Select>
                          </FormControl>

                          {/* Date range label right under picker */}
                          <MDTypography
                            variant="body2"
                            sx={{ fontWeight: 500, color: "text.secondary", mt: 0.5 }}
                          >
                            {dateRangeLabel}
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      {/* Legend chips - center aligned + graph color */}
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        gap={1.5}
                        flexWrap="wrap"
                        mb={2}
                      >
                        {topics.map((t) => (
                          <Chip
                            key={t}
                            size="small"
                            label={t}
                            sx={{
                              bgcolor: `${colorFor(t)}20`, // light background based on line color
                              color: colorFor(t),
                              fontWeight: 600,
                            }}
                            icon={
                              <LocalHospitalIcon
                                sx={{ bgcolor: `${colorFor(t)}20`, color: colorFor(t) }}
                              />
                            }
                          />
                        ))}
                        <Chip
                          size="small"
                          label="Alerts / Emergency Events"
                          icon={<ReportProblemIcon color="error" />}
                          sx={{ bgcolor: "#fff1f2", color: "#7f1d1d", fontWeight: 600 }}
                        />
                      </MDBox>
                      <ResponsiveContainer width="100%" height={360}>
                        <LineChart data={trendSeries} onMouseLeave={() => setIsAlertHover(false)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis domain={[0, 6]} ticks={[1, 2, 3, 4, 5]} />

                          {/* 4) Tooltip renders differently based on the flag */}
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!(active && payload?.length)) return null;
                              const data = payload[0].payload;

                              // If we're hovering the alert icon, show ONLY alerts
                              if (isAlertHover) {
                                return data.alertTopics?.length ? (
                                  <div
                                    style={{
                                      background: "white",
                                      padding: 8,
                                      border: "1px solid #ddd",
                                    }}
                                  >
                                    <div>Date: {label}</div>
                                    <div
                                      style={{ marginTop: 6, color: "#dc2626", fontWeight: 600 }}
                                    >
                                      Alert: Emergent health Issue reported
                                    </div>
                                  </div>
                                ) : null;
                              }

                              // Otherwise, show the normal topics tooltip
                              return (
                                <div
                                  style={{
                                    background: "white",
                                    padding: 8,
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  <div>Date: {label}</div>
                                  {topics
                                    .map((t) => [t, data?.[t]])
                                    .filter(
                                      ([, v]) =>
                                        !(v == null || (typeof v === "number" && Number.isNaN(v)))
                                    )
                                    .map(([t, v]) => (
                                      <div key={t}>
                                        {t}: {v}
                                      </div>
                                    ))}
                                </div>
                              );
                            }}
                          />

                          <Legend />

                          {topics.map((t) => (
                            <Line
                              key={t}
                              type="monotone"
                              legendType="none"
                              dataKey={t}
                              name={t}
                              stroke={colorFor(t)}
                              strokeWidth={2}
                              dot
                              isAnimationActive={false}
                              connectNulls={false}
                            />
                          ))}

                          {/* Alert marker line renders ONLY dots; each dot toggles the flag */}
                          <Line
                            dataKey="hasAlert"
                            legendType="none"
                            tooltipType="none"
                            stroke="none"
                            dot={AlertDot}
                            isAnimationActive={false}
                            connectNulls={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>

                      {/* Scale Section */}
                      <MDBox mt={2} textAlign="center">
                        <MDTypography variant="h6" gutterBottom>
                          Severity Scale
                        </MDTypography>
                        <MDBox display="flex" justifyContent="center" gap={3}>
                          <MDTypography variant="body2">
                            <strong>1</strong> – Very Poor
                          </MDTypography>
                          <MDTypography variant="body2">
                            <strong>2</strong> – Poor
                          </MDTypography>
                          <MDTypography variant="body2">
                            <strong>3</strong> – Fair
                          </MDTypography>
                          <MDTypography variant="body2">
                            <strong>4</strong> – Good
                          </MDTypography>
                          <MDTypography variant="body2">
                            <strong>5</strong> – Very Good
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      {/* Key Observations */}
                      {/* Key Observations */}
                      <MDBox
                        mt={3}
                        p={{ xs: 1.5, sm: 2 }}
                        sx={{ bgcolor: "#f8fafc", borderRadius: 2, overflowX: "hidden" }}
                      >
                        <MDTypography
                          variant="subtitle2"
                          sx={{
                            mb: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontWeight: 600,
                          }}
                        >
                          <InsightsIcon color="primary" sx={{ fontSize: 20 }} /> Key Observations
                        </MDTypography>

                        {topics.map((t) => {
                          const dist = scoreDistribution(t);
                          const lowDays = (dist[0]?.count || 0) + (dist[1]?.count || 0);
                          const goodDays = (dist[3]?.count || 0) + (dist[4]?.count || 0);
                          const total = dist.reduce((sum, d) => sum + d.count, 0);
                          const avg =
                            total > 0
                              ? (
                                  rows
                                    .filter(
                                      (r) =>
                                        r.topic === t &&
                                        r.status !== "NoAnswerOrVoicemail" &&
                                        r.score != null
                                    )
                                    .reduce((s, r) => s + r.score, 0) /
                                  rows.filter(
                                    (r) =>
                                      r.topic === t &&
                                      r.status !== "NoAnswerOrVoicemail" &&
                                      r.score != null
                                  ).length
                                ).toFixed(2)
                              : "-";

                          // alert days list for this topic
                          const alertDays = [
                            ...new Set(
                              rows
                                .filter(
                                  (r) =>
                                    r.topic === t &&
                                    (r.score ?? r.Score ?? 0) > 0 &&
                                    (r.score ?? r.Score ?? 0) < 3
                                )
                                .map((r) => fmtShort(r.date))
                            ),
                          ];

                          const avgNum = typeof avg === "string" ? Number(avg) : avg;
                          const avgColor =
                            !isNaN(avgNum) && avgNum < 3
                              ? "#dc2626" // red for low average
                              : !isNaN(avgNum) && avgNum < 4
                              ? "#f59e0b" // amber for mid
                              : "#16a34a"; // green for good

                          return (
                            <MDBox
                              key={t}
                              display="flex"
                              flexDirection={{ xs: "column", sm: "row" }}
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              gap={1}
                              sx={{
                                p: 1.5,
                                mb: 1.25,
                                bgcolor: "#ffffff",
                                border: "1px solid #e5e7eb",
                                borderRadius: 2,
                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                              }}
                            >
                              <Chip
                                size="small"
                                label={t}
                                sx={{
                                  bgcolor: `${colorFor(t)}20`,
                                  color: colorFor(t),
                                  fontWeight: 600,
                                  fontSize: 13,
                                  px: 1,
                                  borderRadius: 1,
                                  minWidth: { xs: "100%", sm: 160 },
                                }}
                              />
                              <MDTypography
                                variant="body2"
                                color="textPrimary"
                                sx={{ fontSize: 13, lineHeight: 1.6 }}
                              >
                                {lowDays} low-score day(s), {goodDays} good/very-good day(s),
                                average{" "}
                                <b style={{ color: isNaN(avgNum) ? undefined : avgColor }}>{avg}</b>
                                {alertDays.length ? (
                                  <>
                                    {" "}
                                    | Alerts on:{" "}
                                    <b style={{ color: "#b91c1c" }}>{alertDays.join(", ")}</b>
                                  </>
                                ) : null}
                                .
                              </MDTypography>
                            </MDBox>
                          );
                        })}
                      </MDBox>

                      {/* ========== HEALTH INSIGHTS SECTION ========== */}
                      <MDBox
                        mt={4}
                        p={3}
                        sx={{
                          bgcolor: "#fafafa",
                          borderRadius: "24px",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {/* Header */}
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-start"
                          mb={3}
                        >
                          <MDTypography variant="h6" sx={{ fontWeight: "bold" }}>
                            Health Insights
                          </MDTypography>

                          <IconButton size="small" sx={{ p: 0.2, ml: 1, position: "relative" }}>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 20,
                                color: "#6b7280",
                                cursor: "help",
                                "&:hover + .hoverTip": { opacity: 1, visibility: "visible" },
                              }}
                            />
                            <MDBox
                              className="hoverTip"
                              sx={{
                                position: "absolute",
                                top: "130%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                bgcolor: "#ffffff",
                                color: "#111827",
                                px: 2,
                                py: 1.5,
                                borderRadius: "10px",
                                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                fontSize: "0.88rem",
                                lineHeight: 1.5,
                                width: "340px",
                                textAlign: "left",
                                opacity: 0,
                                visibility: "hidden",
                                transition: "opacity 0.25s ease, visibility 0.25s ease",
                                pointerEvents: "none",
                                zIndex: 20,
                              }}
                            >
                              Your conversations translate to an evidence-based severity scale which
                              allows Heartline to track the status of your conditions consistently
                              and accurately.
                              <br />
                              <br />
                              To improve health analysis and insights, we use your conversational
                              transcripts to provide context and understanding about the issues
                              affecting your health status.
                              <br />
                              <br />
                              Heartline applies structured correlation weighting, temporal context,
                              and explicit protective/risk labeling to generate{" "}
                              <strong>clinically intelligent synthesis</strong> for your condition
                              insights.
                            </MDBox>
                          </IconButton>
                        </MDBox>

                        <Grid container spacing={3}>
                          {/* ================= LEFT COLUMN ================= */}
                          {/* ================= LEFT COLUMN ================= */}
                          <Grid item xs={12} md={7}>
                            {loadingInsights ? (
                              <MDBox
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                py={6}
                                sx={{
                                  bgcolor: "#fff",
                                  borderRadius: "16px",
                                  border: "1px solid #e5e7eb",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                }}
                              >
                                <FavoriteIcon
                                  sx={{
                                    fontSize: 36,
                                    color: "rgb(124, 45, 18)",
                                    animation: "heartbeat 1.6s ease-in-out infinite",
                                    "@keyframes heartbeat": {
                                      "0%, 100%": { transform: "scale(1)", opacity: 0.8 },
                                      "25%": { transform: "scale(1.15)", opacity: 1 },
                                      "50%": { transform: "scale(1.3)", opacity: 0.9 },
                                      "75%": { transform: "scale(1.15)", opacity: 1 },
                                    },
                                  }}
                                />
                                <MDTypography variant="body2" color="textSecondary" align="center">
                                  We’re carefully reviewing your recent health data to prepare
                                  meaningful insights. This may take a moment.
                                </MDTypography>
                              </MDBox>
                            ) : (
                              <>
                                {/* 🔹 Summary Notes */}
                                <MDBox
                                  mb={2}
                                  p={2.5}
                                  sx={{
                                    bgcolor: "#fff",
                                    borderRadius: "16px",
                                    border: "1px solid #e5e7eb",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                  }}
                                >
                                  <MDTypography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 1,
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    <NotesIcon color="primary" /> Summary Notes
                                  </MDTypography>
                                  <MDTypography
                                    variant="body2"
                                    color="textSecondary"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        insights?.aggregate?.summary ||
                                        "<p>No summary notes available.</p>",
                                    }}
                                  />
                                </MDBox>

                                {/* 🔹 Family & Caregiver Notes */}
                                <MDBox
                                  mb={2}
                                  p={2.5}
                                  sx={{
                                    bgcolor: "#fff",
                                    borderRadius: "16px",
                                    border: "1px solid #e5e7eb",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                  }}
                                >
                                  <MDTypography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 1,
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    <FamilyRestroomIcon color="secondary" /> Family & Caregiver
                                    Notes
                                  </MDTypography>
                                  <MDTypography
                                    variant="body2"
                                    color="textSecondary"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        insights?.aggregate?.caregiverInsight ||
                                        "<p>No caregiver notes available.</p>",
                                    }}
                                  />
                                </MDBox>

                                {/* 🔹 Doctor Notes */}
                                <MDBox
                                  p={2.5}
                                  sx={{
                                    bgcolor: "#fff",
                                    borderRadius: "16px",
                                    border: "1px solid #e5e7eb",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                  }}
                                >
                                  <MDTypography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 1,
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    <LocalHospitalIcon color="primary" /> Doctor Notes
                                  </MDTypography>
                                  <MDTypography
                                    variant="body2"
                                    color="textSecondary"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        insights?.aggregate?.providerInsight ||
                                        "<p>No doctor notes available.</p>",
                                    }}
                                  />
                                </MDBox>
                              </>
                            )}
                          </Grid>

                          {/* ================= RIGHT COLUMN ================= */}
                          <Grid item xs={12} md={5}>
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                height: "100%",
                                bgcolor: "#fff",
                                borderRadius: "24px",
                                border: "1px solid #e5e7eb",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                p: 3,
                              }}
                            >
                              {dailyOutcomes && (
                                <OutcomesDonut
                                  data={[
                                    { name: "Completed", value: dailyOutcomes.completed },
                                    { name: "Incomplete", value: dailyOutcomes.incomplete },
                                    { name: "No Answer", value: dailyOutcomes.noAnswer },
                                  ]}
                                />
                              )}

                              <MDBox mt={2}>
                                <MDTypography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                  Call Outcomes (last {dailyOutcomes.byDate.size} days)
                                </MDTypography>

                                <MDBox display="flex" flexDirection="column" gap={1.5}>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <CheckCircleIcon color="success" />
                                    <MDTypography variant="body2" sx={{ fontWeight: 500 }}>
                                      Completed: {dailyOutcomes.completed}
                                    </MDTypography>
                                  </MDBox>

                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <ErrorIcon color="warning" />
                                    <MDTypography variant="body2" sx={{ fontWeight: 500 }}>
                                      Incomplete: {dailyOutcomes.incomplete}
                                    </MDTypography>
                                  </MDBox>

                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <PhoneMissedIcon color="error" />
                                    <MDTypography variant="body2" sx={{ fontWeight: 500 }}>
                                      No Answer: {dailyOutcomes.noAnswer}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              </MDBox>
                            </MDBox>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </MDBox>
                  )}

                  {/* ========== WEEKLY SUMMARY ========== */}
                  {tab === 1 && (
                    <MDBox>
                      <MDTypography variant="h6" gutterBottom>
                        Weekly Summary
                      </MDTypography>
                      <Grid container spacing={2}>
                        {topics.map((t) => {
                          const kpi = weeklyKPIs[t] || { avg: 0, delta: 0 };
                          const Up = kpi.delta >= 0.25;
                          const Down = kpi.delta <= -0.25;
                          return (
                            <Grid key={t} item xs={12} md={4}>
                              <MDBox p={2} sx={{ border: "1px solid #e5e7eb", borderRadius: 2 }}>
                                <MDTypography
                                  variant="subtitle2"
                                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                >
                                  <LocalHospitalIcon sx={{ color: colorFor(t) }} /> {t}
                                </MDTypography>
                                <MDTypography variant="body2" color="textSecondary">
                                  Avg (last 7d): <b>{kpi.avg.toFixed(2)}</b>{" "}
                                  {Up && <ArrowUpwardIcon color="success" fontSize="small" />}
                                  {Down && <ArrowDownwardIcon color="error" fontSize="small" />}
                                  {!Up && !Down && (
                                    <InfoOutlinedIcon color="action" fontSize="small" />
                                  )}{" "}
                                  Δ {kpi.delta.toFixed(2)} vs prior 7d
                                </MDTypography>
                              </MDBox>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </MDBox>
                  )}

                  {/* ========== PER-TOPIC FOCUS ========== */}
                  {topics.map((topic, idx) => {
                    const topicTabIndex = 2 + idx;
                    if (tab !== topicTabIndex) return null;

                    const topicRows = rows.filter(
                      (r) =>
                        r.topic === topic && r.status !== "NoAnswerOrVoicemail" && r.score != null
                    );
                    const dist = scoreDistribution(topic);
                    const latest = topicRows.length ? topicRows[topicRows.length - 1] : null;
                    const latestLabel = latest ? SCORE_LABEL[clamp(latest.score, 1, 5) - 1] : "—";
                    const topicKey = findMatchingTopicKey(insights?.topics, topic);
                    const t = topicKey ? insights.topics[topicKey] : null;
                    return (
                      <MDBox key={topic}>
                        <MDTypography variant="h6" gutterBottom>
                          {topic} Focus
                        </MDTypography>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={8}>
                            <ResponsiveContainer width="100%" height={320}>
                              <LineChart
                                data={topicRows.map((r) => ({
                                  date: fmtShort(r.date),
                                  score: r.score,
                                }))}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} />
                                <Tooltip />
                                <Legend />
                                <Line
                                  type="monotone"
                                  dataKey="score"
                                  name="Score"
                                  stroke={colorFor(topic)}
                                  strokeWidth={2}
                                  dot
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <ResponsiveContainer width="100%" height={320}>
                              <BarChart
                                data={dist.map((d) => ({ ...d, label: SCORE_LABEL[d.score - 1] }))}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="score" />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="count" name="Days" fill="#64748b" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Grid>
                        </Grid>

                        <MDBox mt={2} p={2} sx={{ border: "1px solid #e5e7eb", borderRadius: 2 }}>
                          <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                            <LocalHospitalIcon sx={{ color: colorFor(topic) }} />
                            <MDTypography variant="subtitle2">Latest</MDTypography>
                            <Chip
                              size="small"
                              label={
                                latest ? `${latest.score} · ${latestLabel}` : "No recent value"
                              }
                              sx={{ ml: 1, bgcolor: "#f3f4f6" }}
                            />
                          </MDBox>
                          <MDBox mt={2} p={2} sx={{ border: "1px solid #e5e7eb", borderRadius: 2 }}>
                            {/* If insights still loading */}
                            {loadingInsights ? (
                              <MDBox
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                py={3}
                              >
                                <FavoriteIcon
                                  sx={{
                                    fontSize: 36,
                                    color: "rgb(124, 45, 18)",
                                    animation: "heartbeat 1.6s ease-in-out infinite",
                                    "@keyframes heartbeat": {
                                      "0%, 100%": { transform: "scale(1)", opacity: 0.8 },
                                      "25%": { transform: "scale(1.15)", opacity: 1 },
                                      "50%": { transform: "scale(1.3)", opacity: 0.9 },
                                      "75%": { transform: "scale(1.15)", opacity: 1 },
                                    },
                                  }}
                                />
                                <MDTypography variant="body2" color="textSecondary">
                                  We’re carefully reviewing your recent health data to prepare
                                  meaningful insights. This may take a moment.
                                </MDTypography>
                              </MDBox>
                            ) : (
                              <>
                                <MDTypography
                                  variant="subtitle1"
                                  sx={{ fontWeight: "bold" }}
                                  gutterBottom
                                >
                                  {topicKey || topic} Insights
                                </MDTypography>

                                {/* 🔹 Trend Summary */}
                                <MDTypography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                  <strong>Trend Summary:</strong> {t?.trendSummary || "—"}
                                </MDTypography>

                                {/* 🔹 Latest Score & Label */}
                                {/* <MDTypography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                      <strong>Latest Score:</strong> {t?.latestScore ?? "—"} (
                                      {t?.latestLabel || "—"})
                                    </MDTypography> */}

                                {/* 🔹 Caregiver Insight */}
                                <MDTypography
                                  variant="subtitle2"
                                  sx={{ mt: 2, fontWeight: "bold" }}
                                >
                                  Caregiver Insight
                                </MDTypography>
                                <MDTypography
                                  variant="body2"
                                  color="textSecondary"
                                  dangerouslySetInnerHTML={{
                                    __html: t?.caregiverInsight || "<p>No caregiver insight.</p>",
                                  }}
                                />

                                {/* 🔹 Provider Insight */}
                                <MDTypography
                                  variant="subtitle2"
                                  sx={{ mt: 2, fontWeight: "bold" }}
                                >
                                  Provider Insight
                                </MDTypography>
                                <MDTypography
                                  variant="body2"
                                  color="textSecondary"
                                  dangerouslySetInnerHTML={{
                                    __html: t?.providerInsight || "<p>No provider insight.</p>",
                                  }}
                                />

                                {/* 🔹 Data Gap Note */}
                                {t?.dataGapNote && (
                                  <>
                                    <MDTypography
                                      variant="subtitle2"
                                      sx={{ mt: 2, fontWeight: "bold" }}
                                    >
                                      Data Gap Note
                                    </MDTypography>
                                    <MDTypography
                                      variant="body2"
                                      color="textSecondary"
                                      dangerouslySetInnerHTML={{
                                        __html: t?.dataGapNote,
                                      }}
                                    />
                                  </>
                                )}
                              </>
                            )}
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    );
                  })}

                  {/* ========== SENTIMENT ========== */}
                  {tab === topics.length + 2 && (
                    <MDBox>
                      <MDTypography variant="h6" gutterBottom>
                        Sentiment
                      </MDTypography>
                      {(() => {
                        const sMap = {};

                        rows.forEach((r) => {
                          if (r.status === "NoAnswerOrVoicemail" || !r.sentiment) return; // exclude
                          const d = fmtShort(r.date);

                          // Ensure only one sentiment per callId per date
                          if (!sMap[d]) sMap[d] = { date: d, Positive: 0, Neutral: 0, Negative: 0 };

                          if (r.sentiment === "Positive") sMap[d].Positive = 1;
                          else if (r.sentiment === "Neutral") sMap[d].Neutral = 1;
                          else if (r.sentiment === "Negative") sMap[d].Negative = 1;
                        });

                        const data = Object.values(sMap).map((d) => {
                          // only one of these will be 1
                          const score =
                            d.Positive === 1 ? 3 : d.Neutral === 1 ? 2 : d.Negative === 1 ? 1 : 2;
                          return { ...d, sentimentScore: score };
                        });

                        // Totals by count of calls
                        const totals = data.reduce(
                          (acc, d) => ({
                            Positive: acc.Positive + d.Positive,
                            Neutral: acc.Neutral + d.Neutral,
                            Negative: acc.Negative + d.Negative,
                          }),
                          { Positive: 0, Neutral: 0, Negative: 0 }
                        );

                        const getSentimentLabel = (v) =>
                          v === 3 ? "Positive" : v === 2 ? "Neutral" : "Negative";

                        return (
                          <>
                            <ResponsiveContainer width="100%" height={320}>
                              <LineChart
                                margin={{ top: 8, right: 16, bottom: 8, left: 72 }}
                                data={data}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis
                                  domain={[1, 3]}
                                  ticks={[1, 2, 3]}
                                  tickFormatter={(v) =>
                                    v === 1 ? "Negative" : v === 2 ? "Neutral" : "Positive"
                                  }
                                  padding={{ top: 10, bottom: 10 }}
                                />
                                <Tooltip
                                  formatter={(value) => [getSentimentLabel(value), "Sentiment"]}
                                  labelFormatter={(label) => `Date: ${label}`}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="sentimentScore"
                                  stroke="#2563eb"
                                  strokeWidth={2}
                                  dot={{ r: 3 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>

                            {/* Totals summary */}
                            <MDBox
                              mt={2}
                              display="flex"
                              justifyContent="center" // center horizontally
                              gap={3} // spacing between items
                              flexWrap="wrap" // wrap on smaller screens
                            >
                              <MDBox display="flex" alignItems="center" gap={1}>
                                <SentimentSatisfiedAltIcon color="success" />
                                <MDTypography variant="body2">
                                  Positive: {totals.Positive}
                                </MDTypography>
                              </MDBox>
                              <MDBox display="flex" alignItems="center" gap={1}>
                                <SentimentNeutralIcon color="action" />
                                <MDTypography variant="body2">
                                  Neutral: {totals.Neutral}
                                </MDTypography>
                              </MDBox>
                              <MDBox display="flex" alignItems="center" gap={1}>
                                <SentimentVeryDissatisfiedIcon color="error" />
                                <MDTypography variant="body2">
                                  Negative: {totals.Negative}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          </>
                        );
                      })()}
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}
